import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import TextType from './TextType';

export const IndustrySection: React.FC = () => {
  const [activeTab, setActiveTab] = useState('Healthcare');
  const navigate = useNavigate();

  const categories = ['Food & Beverage', 'Healthcare', 'Cosmetics'];

  const products = [
    // Healthcare (Existing)
    {
      title: 'Medicine cartons',
      description: 'Medicine cartons, blister pack outer boxes.',
      image: '/Images/p_one.webp',
      category: 'Healthcare'
    },
    {
      title: 'Medical device boxes',
      description: 'Medicine cartons, blister pack outer boxes.',
      image: '/Images/p_two.webp',
      category: 'Healthcare'
    },
    {
      title: 'Hospital supply cartons',
      description: 'Medicine cartons, blister pack outer boxes.',
      image: '/Images/p_three.webp',
      category: 'Healthcare'
    },
    // Food & Beverage (New)
    {
      title: 'Food Packaging',
      description: 'Premium food grade packaging solutions.',
      image: '/Images/Box 11.webp',
      category: 'Food & Beverage'
    },
    {
      title: 'Beverage Cartons',
      description: 'Durable and attractive beverage boxes.',
      image: '/Images/Box 13.webp',
      category: 'Food & Beverage'
    },
    {
      title: 'Custom Food Boxes',
      description: 'Customized packaging for food products.',
      image: '/Images/Box 15.webp',
      category: 'Food & Beverage'
    },
    // Cosmetics (New)
    {
      title: 'Cosmetic Boxes',
      description: 'Luxury packaging for cosmetic products.',
      image: '/Images/Box 10.webp',
      category: 'Cosmetics'
    },
    {
      title: 'Beauty Kits',
      description: 'Elegant boxes for beauty collections.',
      image: '/Images/Box 12.webp',
      category: 'Cosmetics'
    },
    {
      title: 'Skincare Packaging',
      description: 'Premium cartons for skincare items.',
      image: '/Images/Box 14.webp',
      category: 'Cosmetics'
    }
  ];

  const filteredProducts = products.filter(product => product.category === activeTab);

  return (
    <section className="py-20">
      <div className="container mx-auto px-4 text-center rounded-3xl py-20" style={{ background: 'linear-gradient(0deg, #FEEBF4 0%, #FFFFFF 100%)' }}>
        <motion.div 
          className="mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
        >
           <div className="flex justify-center mb-6">
              <img src="/Images/industry_client.webp" alt="Industry Clients" className="h-12 object-contain" />
           </div>
           <h2 
             className="mb-6"
             style={{
               fontFamily: 'Nanum Myeongjo',
               fontWeight: 800,
               fontSize: '52px',
               lineHeight: '67.6px',
               letterSpacing: '0%',
               textAlign: 'center',
               color: '#2A2A2A'
             }}
           >
             <TextType
               baseOpacity={0}
               enableBlur={true}
               baseRotation={5}
               blurStrength={10}
               size="lg"
               duration={1}
               textClassName="font-bold text-[32px] md:text-[52px] leading-[40px] md:leading-[67.6px] text-center text-[#2A2A2A]"
             >
               High-precision packaging designed for pharmaceuticals, food & beverages, cosmetics, electronics, and more.
             </TextType>
           </h2>
        </motion.div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-between items-center gap-4 mb-12">
          <div className="flex items-center gap-4 bg-white/50 p-1.5 rounded-full backdrop-blur-sm">
            {categories.map((cat) => (
              <div key={cat} className="relative">
                {activeTab === cat && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute inset-0 rounded-full shadow-md"
                    style={{ background: 'linear-gradient(88.69deg, #CA1167 -1.27%, #F47749 98.89%)' }}
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
                <button
                  onClick={() => setActiveTab(cat)}
                  className={`relative z-10 px-6 py-2 rounded-full text-sm font-medium transition-colors duration-300 ${
                    activeTab === cat ? 'text-white' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {cat}
                </button>
              </div>
            ))}
          </div>
          <div 
            className="flex items-center gap-2 cursor-pointer group"
            onClick={() => navigate('/products')}
          >
            <button 
              className="cursor-target rounded-full text-text-muted group-hover:bg-[#FF309D] group-hover:text-white transition-all duration-300 px-6 py-2"
              style={{ border: '1px solid #FF309D' }}
            >
               Explore All Categories 
            </button>
            <div className="w-6 h-6 rounded-full flex items-center justify-center transition-colors duration-300 group-hover:bg-[#FF309D]" style={{ background: '#FDC7DF' }}>
              <ArrowRight size={16} className="group-hover:text-white" />
            </div>
          </div>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 min-h-[400px]">
          <AnimatePresence mode="wait">
            {filteredProducts.map((product, index) => (
              <motion.div
                key={`${activeTab}-${index}`}
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="cursor-target bg-white rounded-3xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 group relative overflow-hidden"
              >
                {/* Shine Effect */}
                <div className="absolute inset-0 -translate-x-[150%] skew-x-12 group-hover:translate-x-[150%] transition-transform duration-1000 ease-in-out bg-gradient-to-r from-transparent via-white/40 to-transparent z-10 pointer-events-none" />
                
                <div className="w-full aspect-square rounded-2xl mb-6 overflow-hidden group-hover:scale-105 transition-transform duration-500 relative z-0">
                  <img 
                    src={product.image} 
                    alt={product.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-heading font-bold mb-2">{product.title}</h3>
                <p className="text-sm text-text-muted">{product.description}</p>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
